/**
 * FlWizard - Touch-optimierte Vorarbeiter/FL-Erfassung
 * 
 * Legacy-Style UI mit Card-Layout:
 * 1. Strecke wählen
 * 2. Sub-Strecke wählen (wenn vorhanden)
 * 3. Kunde wählen
 * 4. Gewicht eingeben
 * 5. Bestätigen
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  Check,
  Scale,
  Loader2,
  Route,
  Layers,
  Building2,
  Search,
  Clock,
  Info,
  Link as LinkIcon,
} from 'lucide-react';
import { Stepper } from './Stepper';
import { GewichtRueckfrage, RUECKFRAGE_AB_KG } from './GewichtRueckfrage';

interface SubLine {
  id: string;
  name: string;
}

interface Line {
  id: string;
  name: string;
  subLines?: SubLine[];
}

interface Customer {
  id: string;
  name: string;
  shortName: string | null;
  itemType?: 'customer' | 'group';
}

interface CustomerGroup {
  id: string;
  name: string;
}

// NEU: LaundryClass Entity (statt Enum)
interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
  // NEU: Pro-LaundryClass Artikelgruppen-Pflicht (überschreibt global)
  requiresArticleGroup?: boolean;
  // NEU: Pro-LaundryClass erlaubte Artikelgruppen
  allowedArticleGroups?: ArticleGroupEntity[];
}

// Artikelgruppe Entity
interface ArticleGroupEntity {
  id: string;
  code: string;
  name: string;
}

interface CaptureConfig {
  id: string;
  screen: 'FL' | 'WSA';
  lineId: string | null;
  requiresLaundryClass: boolean;
  requiresArticleGroup: boolean;
  allowedLaundryClasses: LaundryClassEntity[];
  allowedArticleGroups: ArticleGroupEntity[];
}

interface SessionEntry {
  weightKg: number;
  timestamp: Date;
}

interface FlWizardProps {
  stationId: string | null;
  isAdminMode?: boolean;
  onSuccess: () => void;
  onExtendUnlock: () => void;
}

type Step = 'line' | 'subline' | 'laundryClass' | 'articleGroup' | 'customer' | 'input' | 'confirm';

// Steps werden dynamisch berechnet basierend auf CaptureConfig
const getSteps = (hasLaundryClass: boolean, hasArticleGroup: boolean) => {
  const steps = ['Strecke'];
  if (hasLaundryClass) steps.push('Wäscheklasse');
  if (hasArticleGroup) steps.push('Artikelgruppe');
  steps.push('Kunde', 'Gewicht');
  return steps;
};

const getStepIndex = (step: Step, hasLaundryClass: boolean, hasArticleGroup: boolean): number => {
  let idx = 0;
  if (step === 'line' || step === 'subline') return 0;
  idx++;
  if (hasLaundryClass) {
    if (step === 'laundryClass') return idx;
    idx++;
  }
  if (hasArticleGroup) {
    if (step === 'articleGroup') return idx;
    idx++;
  }
  if (step === 'customer') return idx;
  idx++;
  if (step === 'input' || step === 'confirm') return idx;
  return idx;
};

export function FlWizard({ stationId, isAdminMode, onSuccess, onExtendUnlock }: FlWizardProps) {
  // Stammdaten
  const [lines, setLines] = useState<Line[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [topUsage, setTopUsage] = useState<Array<{ id: string; displayName: string; kind: 'customer' | 'group' }>>([]); 
  const [loading, setLoading] = useState(true);

  // Kundensuche
  const [customerSearch, setCustomerSearch] = useState('');

  // Gefilterte Kunden + Gruppen (case-insensitive Suche)
  const filteredResults = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return [];
    
    // Gruppen durchsuchen
    const matchingGroups = customerGroups
      .filter(g => g.name.toLowerCase().includes(q))
      .map(g => ({
        id: `group:${g.id}`,
        name: g.name,
        shortName: null as string | null,
        itemType: 'group' as const,
      }));
    
    // Kunden durchsuchen
    const matchingCustomers = customers
      .filter(c => c.name.toLowerCase().includes(q) || c.shortName?.toLowerCase().includes(q))
      .map(c => ({
        ...c,
        itemType: (c.itemType || 'customer') as 'customer' | 'group',
      }));
    
    // Kombiniert, sortiert, limitiert
    return [...matchingGroups, ...matchingCustomers]
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .slice(0, 50);
  }, [customers, customerGroups, customerSearch]);

  // Wizard State
  const [step, setStep] = useState<Step>('line');
  const [selectedLine, setSelectedLine] = useState<Line | null>(null);
  const [selectedSubLine, setSelectedSubLine] = useState<SubLine | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [weight, setWeight] = useState('');

  // Wäscheklasse State
  const [captureConfig, setCaptureConfig] = useState<CaptureConfig | null>(null);
  const [selectedLaundryClass, setSelectedLaundryClass] = useState<LaundryClassEntity | null>(null);
  const [allLaundryClasses, setAllLaundryClasses] = useState<LaundryClassEntity[]>([]);

  // Artikelgruppe State
  const [selectedArticleGroup, setSelectedArticleGroup] = useState<ArticleGroupEntity | null>(null);
  const [allArticleGroups, setAllArticleGroups] = useState<ArticleGroupEntity[]>([]);

  // Dynamische Steps basierend auf CaptureConfig
  const requiresLaundryClass = captureConfig?.requiresLaundryClass ?? false;
  const requiresArticleGroup = captureConfig?.requiresArticleGroup ?? false;
  const allowedLaundryClasses = captureConfig?.allowedLaundryClasses ?? [];
  const allowedArticleGroups = captureConfig?.allowedArticleGroups ?? [];
  const dynamicSteps = getSteps(requiresLaundryClass, requiresArticleGroup);
  const currentStepIndex = getStepIndex(step, requiresLaundryClass, requiresArticleGroup);

  // Submit State
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Session-Einträge (für "+ Nächster Zettel" Anzeige)
  const [sessionEntries, setSessionEntries] = useState<SessionEntry[]>([]);

  // Ref für verstecktes Input-Feld (Keyboard-Capture)
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Stammdaten laden
  const loadMasterData = useCallback(async () => {
    setLoading(true);
    try {
      // Top Usage (Quick-Buttons) - Auth ueber STATION-Cookie, Admin via stationId Query
      const topUrl = isAdminMode && stationId
        ? `/api/data/usage/top?stationId=${stationId}&limit=12`
        : '/api/data/usage/top?limit=12';

      const [linesRes, customersRes, groupsRes, topRes, laundryClassesRes, articleGroupsRes] = await Promise.all([
        fetch('/api/data/lines', { credentials: 'include' }),
        fetch('/api/data/customers', { credentials: 'include' }),
        fetch('/api/data/customer-groups', { credentials: 'include' }),
        fetch(topUrl, { credentials: 'include' }),
        fetch('/api/data/laundry-classes', { credentials: 'include' }),
        fetch('/api/data/article-groups', { credentials: 'include' }),
      ]);

      if (linesRes.ok) setLines(await linesRes.json());
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (groupsRes.ok) setCustomerGroups(await groupsRes.json());
      if (topRes?.ok) setTopUsage(await topRes.json());
      
      if (laundryClassesRes?.ok) setAllLaundryClasses(await laundryClassesRes.json());
      if (articleGroupsRes?.ok) setAllArticleGroups(await articleGroupsRes.json());
    } catch (err) {
      console.error('Fehler beim Laden der Stammdaten:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, isAdminMode]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Fokus auf verstecktes Input-Feld wenn step === 'input'
  useEffect(() => {
    if (step === 'input' && hiddenInputRef.current) {
      hiddenInputRef.current.focus();
    }
  }, [step]);

  // Keyboard-Handler für verstecktes Input-Feld
  const handleHiddenInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    // Ziffern 0-9
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      setWeight(prev => prev + key);
    }
    // Dezimalpunkt
    else if (key === '.' || key === ',') {
      e.preventDefault();
      if (!weight.includes('.')) {
        setWeight(prev => prev + '.');
      }
    }
    // Backspace = letzte Ziffer löschen, bei leerem weight letzten Eintrag löschen
    else if (key === 'Backspace') {
      e.preventDefault();
      if (weight.length > 0) {
        setWeight(prev => prev.slice(0, -1));
      } else if (sessionEntries.length > 0) {
        setSessionEntries(prev => prev.slice(0, -1));
      }
    }
    // Delete oder Escape = alles löschen
    else if (key === 'Delete' || key === 'Escape') {
      e.preventDefault();
      setWeight('');
      setSessionEntries([]);
    }
    // + = zur Liste hinzufügen
    else if (key === '+') {
      e.preventDefault();
      if (weight && parseFloat(weight) > 0) {
        handleAddToList();
      }
    }
    // = oder Enter = Speichern
    else if (key === '=' || key === 'Enter') {
      e.preventDefault();
      const hasWeight = weight && parseFloat(weight) > 0;
      const hasEntries = sessionEntries.length > 0;
      if (hasWeight || hasEntries) {
        handleFinalSave();
      }
    }
  };

  // CaptureConfig für Line laden - gibt Config zurück UND setzt State
  const loadCaptureConfig = useCallback(async (lineId: string): Promise<CaptureConfig | null> => {
    try {
      const res = await fetch(`/api/data/capture-config?screen=FL&lineId=${lineId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const config = data.config as CaptureConfig | null;
        setCaptureConfig(config);
        return config;
      } else {
        setCaptureConfig(null);
        return null;
      }
    } catch {
      setCaptureConfig(null);
      return null;
    }
  }, []);

  // Strecke auswählen
  const selectLine = async (line: Line) => {
    setSelectedLine(line);
    // CaptureConfig für diese Line laden
    const config = await loadCaptureConfig(line.id);
    
    // Wenn Sub-Strecken vorhanden, zeige diese
    if (line.subLines && line.subLines.length > 0) {
      setStep('subline');
    } else {
      setSelectedSubLine(null);
      // Direkt mit geladener Config entscheiden (nicht auf State warten)
      proceedAfterSubLine(config);
    }
  };

  // Nach SubLine-Auswahl: Wäscheklasse → Artikelgruppe → Kunde
  // config Parameter für direkten Aufruf nach loadCaptureConfig (State noch nicht aktualisiert)
  const proceedAfterSubLine = (config?: CaptureConfig | null) => {
    // Nutze übergebene Config oder fallback auf State
    const effectiveConfig = config !== undefined ? config : captureConfig;
    if (effectiveConfig?.requiresLaundryClass) {
      setStep('laundryClass');
    } else if (effectiveConfig?.requiresArticleGroup) {
      setStep('articleGroup');
    } else {
      setStep('customer');
    }
  };

  // Nach Wäscheklasse-Auswahl: Artikelgruppe oder Kunde
  // NEU: Pro-LaundryClass requiresArticleGroup überschreibt global
  const proceedAfterLaundryClass = (chosenLc: LaundryClassEntity) => {
    // Finde pro-LC Record in allowedLaundryClasses
    const lcRecord = allowedLaundryClasses.find(lc => lc.id === chosenLc.id);
    
    // Effective requiresArticleGroup: Pro-LC > Global Fallback
    const effectiveRequiresAG = lcRecord?.requiresArticleGroup ?? requiresArticleGroup;
    
    if (effectiveRequiresAG) {
      setStep('articleGroup');
    } else {
      setStep('customer'); // Skip Artikelgruppe (z.B. Heimbewohner)
    }
  };

  // Navigation
  const goBack = () => {
    if (step === 'subline') {
      setStep('line');
      setSelectedLine(null);
      setCaptureConfig(null);
    } else if (step === 'laundryClass') {
      if (selectedLine?.subLines && selectedLine.subLines.length > 0) {
        setStep('subline');
        setSelectedSubLine(null);
      } else {
        setStep('line');
        setSelectedLine(null);
        setCaptureConfig(null);
      }
      setSelectedLaundryClass(null);
    } else if (step === 'articleGroup') {
      if (requiresLaundryClass) {
        setStep('laundryClass');
        setSelectedLaundryClass(null);
      } else if (selectedLine?.subLines && selectedLine.subLines.length > 0) {
        setStep('subline');
        setSelectedSubLine(null);
      } else {
        setStep('line');
        setSelectedLine(null);
        setCaptureConfig(null);
      }
      setSelectedArticleGroup(null);
    } else if (step === 'customer') {
      const lcRecord = allowedLaundryClasses.find(a => a.id === selectedLaundryClass?.id);
      const effectiveRequiresAG = lcRecord?.requiresArticleGroup ?? requiresArticleGroup;
      if (effectiveRequiresAG) {
        setStep('articleGroup');
        setSelectedArticleGroup(null);
      } else if (requiresLaundryClass) {
        setStep('laundryClass');
        setSelectedLaundryClass(null);
      } else if (selectedLine?.subLines && selectedLine.subLines.length > 0) {
        setStep('subline');
        setSelectedSubLine(null);
      } else {
        setStep('line');
        setSelectedLine(null);
        setCaptureConfig(null);
      }
    } else if (step === 'input') {
      setStep('customer');
      setSelectedCustomer(null);
    } else if (step === 'confirm') {
      setStep('input');
    }
  };

  const resetForm = () => {
    setStep('line');
    setSelectedLine(null);
    setSelectedSubLine(null);
    setSelectedLaundryClass(null);
    setSelectedArticleGroup(null);
    setCaptureConfig(null);
    setSelectedCustomer(null);
    setWeight('');
    setError('');
    setSuccess(false);
    setSessionEntries([]);
  };

  // Speichern (Basis-Funktion)
  const doSave = async (): Promise<boolean> => {
    if (!selectedLine || !selectedCustomer || !weight) return false;

    setSubmitting(true);
    setError('');

    try {
      const url = isAdminMode
        ? '/api/processing-entries'
        : `/api/stations/${stationId}/processing-entries`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lineId: selectedLine.id,
          subLineId: selectedSubLine?.id || null,
          customerId: selectedCustomer.id,
          weightKg: parseFloat(weight),
          ...(selectedLaundryClass ? { laundryClassId: selectedLaundryClass.id } : {}),
          ...(selectedArticleGroup ? { articleGroupId: selectedArticleGroup.id } : {}),
          ...(isAdminMode && stationId ? { stationId } : {}),
        }),
      });

      if (res.ok) {
        onExtendUnlock();
        return true;
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Speichern');
        return false;
      }
    } catch {
      setError('Verbindungsfehler');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Speichern + Zurück zu Strecke (bisheriges Verhalten)
  const handleSave = async () => {
    const savedWeight = weight;
    const ok = await doSave();
    if (ok) {
      resetForm();
      onSuccess();
    }
  };

  // + Button: Gewicht lokal zur Liste hinzufügen (kein API-Call)
  const handleAddToList = () => {
    if (!weight || parseFloat(weight) <= 0) return;
    const w = parseFloat(weight);
    setSessionEntries(prev => [...prev, { weightKg: w, timestamp: new Date() }]);
    setWeight('');
  };

  const [gewichtZurBestaetigung, setGewichtZurBestaetigung] = useState<number | null>(null);

  // Speichern: Summe aller Einträge als einen Eintrag an API senden
  const handleFinalSave = async () => {
    // Doppel-Submit-Sperre auch für den Tastatur-Pfad (Enter/=), der nicht
    // über die disabled-Buttons läuft — sonst doppelte Einträge
    if (submitting) return;
    if (!selectedLine || !selectedCustomer) return;
    
    // Aktuelles Gewicht noch zur Liste hinzufügen falls vorhanden
    let totalWeight = sessionEntries.reduce((sum, e) => sum + e.weightKg, 0);
    if (weight && parseFloat(weight) > 0) {
      totalWeight += parseFloat(weight);
    }
    
    if (totalWeight <= 0) return;

    // Weiche Rueckfrage bei ungewoehnlich hoher SUMME — der Taschenrechner-
    // Loop macht aus Zetteln schnell vierstellige Werte, gefragt wird nach
    // der Summe, die tatsaechlich gespeichert wuerde.
    if (totalWeight >= RUECKFRAGE_AB_KG) {
      setGewichtZurBestaetigung(totalWeight);
      return;
    }

    await sendeErfassung(totalWeight);
  };

  const sendeErfassung = async (totalWeight: number) => {
    if (submitting) return;
    if (!selectedLine || !selectedCustomer) return;

    setSubmitting(true);
    setError('');

    try {
      const url = isAdminMode
        ? '/api/processing-entries'
        : `/api/stations/${stationId}/processing-entries`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lineId: selectedLine.id,
          subLineId: selectedSubLine?.id || null,
          customerId: selectedCustomer.id,
          weightKg: totalWeight,
          ...(selectedLaundryClass ? { laundryClassId: selectedLaundryClass.id } : {}),
          ...(selectedArticleGroup ? { articleGroupId: selectedArticleGroup.id } : {}),
          ...(isAdminMode && stationId ? { stationId } : {}),
        }),
      });

      if (res.ok) {
        onExtendUnlock();
        resetForm();
        onSuccess();
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Speichern');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setSubmitting(false);
    }
  };

  // Numpad Handler
  const handleNumpad = (key: string) => {
    if (key === '←') {
      // Backspace: letzte Ziffer löschen, bei leerem weight letzten Eintrag löschen
      if (weight.length > 0) {
        setWeight(prev => prev.slice(0, -1));
      } else if (sessionEntries.length > 0) {
        setSessionEntries(prev => prev.slice(0, -1));
      }
    } else if (key === '.') {
      if (!weight.includes('.')) {
        setWeight(prev => prev + '.');
      }
    } else {
      setWeight(prev => prev + key);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Erfolgs-Anzeige
  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Erfassung gespeichert!</h3>
        <p className="text-gray-500 mt-2">{weight} kg erfasst</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stepper */}
      <Stepper steps={dynamicSteps} currentStep={currentStepIndex} />

      {/* Fehler */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Zurück-Button - Legacy: groß, volle Breite auf Mobile */}
      {step !== 'line' && (
        <button
          onClick={goBack}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 text-lg border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
      )}

      {/* Aktuelle Auswahl - Legacy Badge Style */}
      {(selectedLine || selectedCustomer || weight) && (
        <div className="bg-white rounded-lg border p-3">
          <div className="flex flex-wrap gap-2">
            {selectedLine && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Route className="w-3 h-3" />
                {selectedLine.name}
              </span>
            )}
            {selectedSubLine && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Layers className="w-3 h-3" />
                {selectedSubLine.name}
              </span>
            )}
            {selectedLaundryClass && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border border-blue-200 bg-blue-50 rounded-full text-sm text-blue-700">
                <Layers className="w-3 h-3" />
                {selectedLaundryClass.name}
              </span>
            )}
            {selectedCustomer && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Building2 className="w-3 h-3" />
                {selectedCustomer.shortName || selectedCustomer.name}
              </span>
            )}
            {weight && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Scale className="w-3 h-3" />
                {weight} kg
              </span>
            )}
          </div>
        </div>
      )}

      {/* Schritt 1: Strecke - Legacy Card Style */}
      {step === 'line' && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Route className="w-5 h-5" />
              Strecke wählen
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {lines.map((l) => (
                <button
                  key={l.id}
                  onClick={() => selectLine(l)}
                  className="min-h-[72px] text-lg border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex flex-col items-center justify-center gap-1"
                >
                  <span>{l.name}</span>
                  {l.subLines && l.subLines.length > 0 && (
                    <span className="text-xs text-slate-500">{l.subLines.length} Varianten</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schritt 1b: Sub-Strecke - Legacy Card Style */}
      {step === 'subline' && selectedLine && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Layers className="w-5 h-5" />
              {selectedLine.name} - Variante wählen
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {selectedLine.subLines?.map((sl) => (
                <button
                  key={sl.id}
                  onClick={() => {
                    setSelectedSubLine(sl);
                    proceedAfterSubLine();
                  }}
                  className="min-h-[72px] text-lg border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {sl.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schritt Wäscheklasse (wenn aktiv) */}
      {step === 'laundryClass' && (() => {
        // Wenn eingeschränkt, nur erlaubte Klassen zeigen; sonst alle
        const displayClasses = allowedLaundryClasses.length > 0 ? allowedLaundryClasses : allLaundryClasses;
        return (
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Wäscheklasse wählen
              </h3>
            </div>
            <div className="p-4">
              {displayClasses.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-red-600 font-medium">Keine Wäscheklassen verfügbar</p>
                  <p className="text-sm text-gray-500 mt-1">Bitte Admin kontaktieren</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {displayClasses.map((lc) => (
                    <button
                      key={lc.id}
                      onClick={() => {
                        setSelectedLaundryClass(lc);
                        proceedAfterLaundryClass(lc);
                      }}
                      className="min-h-[72px] text-lg border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center justify-center"
                    >
                      {lc.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Schritt Artikelgruppe (wenn aktiv) */}
      {step === 'articleGroup' && (() => {
        // NEU: Pro-LC Filterung mit Intersection-Logik (analog Backend)
        const lcRecord = allowedLaundryClasses.find(lc => lc.id === selectedLaundryClass?.id);
        const perLcAllowed = lcRecord?.allowedArticleGroups ?? [];
        const globalAllowed = allowedArticleGroups;
        
        // Effektive Whitelist: Intersection wenn beide vorhanden
        let effectiveAllowed: ArticleGroupEntity[];
        if (globalAllowed.length > 0 && perLcAllowed.length > 0) {
          // Intersection: Muss in BEIDEN sein
          const perLcIds = new Set(perLcAllowed.map(ag => ag.id));
          effectiveAllowed = globalAllowed.filter(ag => perLcIds.has(ag.id));
        } else if (perLcAllowed.length > 0) {
          effectiveAllowed = perLcAllowed;
        } else if (globalAllowed.length > 0) {
          effectiveAllowed = globalAllowed;
        } else {
          effectiveAllowed = allArticleGroups; // Keine Einschränkung
        }
        
        const displayGroups = effectiveAllowed;
        return (
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Artikelgruppe wählen
              </h3>
            </div>
            <div className="p-4">
              {displayGroups.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-red-600 font-medium">Keine Artikelgruppen für diese Strecke erlaubt</p>
                  <p className="text-sm text-gray-500 mt-1">Bitte Admin kontaktieren</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {displayGroups.map((ag) => (
                    <button
                      key={ag.id}
                      onClick={() => {
                        setSelectedArticleGroup(ag);
                        setStep('customer');
                      }}
                      className="min-h-[72px] text-lg border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center justify-center"
                    >
                      {ag.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Schritt 2: Kunde - Recent + Suche */}
      {step === 'customer' && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Kunde wählen
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Suchfeld */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Kunde suchen..."
                className="w-full pl-10 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Keine Suche: Schnellwahl anzeigen */}
            {!customerSearch.trim() && topUsage.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <Clock className="w-4 h-4" />
                  Schnellwahl
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {topUsage.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        // id kann "group:xxx" oder normale customerId sein (wie WsaWizard)
                        setSelectedCustomer({ id: item.id, name: item.displayName, shortName: null });
                        setCustomerSearch('');
                        setStep('input');
                      }}
                      className={`min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 ${
                        item.kind === 'group'
                          ? 'bg-purple-50 border border-purple-200 hover:bg-purple-100'
                          : 'bg-green-50 border border-green-200 hover:bg-green-100'
                      }`}
                    >
                      {item.displayName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Keine Suche + keine Schnellwahl: Hinweis */}
            {!customerSearch.trim() && topUsage.length === 0 && (
              isAdminMode && !stationId ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-green-800">
                        Schnellwahl ist im Admin-Testmodus ohne gekoppelte Station deaktiviert.
                      </p>
                      <a
                        href={`/pair?returnTo=${encodeURIComponent(window.location.pathname)}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-green-700 hover:text-green-900"
                      >
                        <LinkIcon className="w-4 h-4" />
                        Dieses Gerät koppeln
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  Noch keine Schnellwahl – erscheint nach ersten Erfassungen
                </p>
              )
            )}

            {/* Mit Suche: gefilterte Liste (Kunden + Gruppen) */}
            {customerSearch.trim() && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                {filteredResults.length === 0 ? (
                  <p className="col-span-full text-center text-gray-500 py-4">
                    Keine Treffer für "{customerSearch}"
                  </p>
                ) : (
                  filteredResults.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedCustomer(c);
                        setCustomerSearch('');
                        setStep('input');
                      }}
                      className={`min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 ${
                        c.itemType === 'group'
                          ? 'bg-purple-50 border border-purple-200 hover:bg-purple-100'
                          : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                      }`}
                    >
                      {c.shortName || c.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schritt 3: Gewicht eingeben - Taschenrechner-Style */}
      {step === 'input' && (
        <div 
          className="bg-white rounded-lg border shadow-sm"
          onClick={() => hiddenInputRef.current?.focus()}
        >
          {/* Verstecktes Input-Feld - NUR Event-Sensor, keine native Textverarbeitung */}
          <input
            ref={hiddenInputRef}
            type="text"
            value=""
            onChange={() => {}}
            className="sr-only"
            onKeyDown={handleHiddenInputKeyDown}
            onBlur={() => setTimeout(() => hiddenInputRef.current?.focus(), 100)}
            autoFocus
          />
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Gewicht
            </h3>
            <span className="text-sm text-slate-500">
              {selectedLine?.name} / {selectedCustomer?.shortName || selectedCustomer?.name}
            </span>
          </div>
          <div className="p-4">
            {/* Taschenrechner-Display: 32+28.5+45+[aktuelle Eingabe] = Summe */}
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg min-h-[80px]">
              {/* Rechenzeile */}
              <div className="text-right text-xl font-mono text-slate-600 overflow-x-auto whitespace-nowrap">
                {sessionEntries.map(e => e.weightKg).join('+')}
                {sessionEntries.length > 0 && '+'}
                <span className="text-slate-900 font-bold">{weight || '0'}</span>
              </div>
              {/* Summenzeile */}
              <div className="text-right text-3xl font-bold text-slate-900 mt-2 border-t border-slate-300 pt-2">
                {(sessionEntries.reduce((sum, e) => sum + e.weightKg, 0) + (parseFloat(weight) || 0)).toFixed(1)}
                <span className="text-lg ml-1 text-slate-400">kg</span>
              </div>
            </div>

            {/* Numpad - Taschenrechner-Layout 4 Spalten */}
            <div className="grid grid-cols-4 gap-2 max-w-sm mx-auto">
              <button onClick={() => handleNumpad('7')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">7</button>
              <button onClick={() => handleNumpad('8')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">8</button>
              <button onClick={() => handleNumpad('9')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">9</button>
              <button onClick={() => handleNumpad('←')} className="min-h-[56px] text-xl font-bold bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 active:bg-slate-300 text-slate-600">
                <ArrowLeft className="w-5 h-5 mx-auto" />
              </button>
              
              <button onClick={() => handleNumpad('4')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">4</button>
              <button onClick={() => handleNumpad('5')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">5</button>
              <button onClick={() => handleNumpad('6')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">6</button>
              <button 
                onClick={handleAddToList}
                disabled={!(parseFloat(weight) > 0)}
                className="min-h-[56px] text-3xl font-bold bg-green-500 text-white rounded-lg hover:bg-green-600 active:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed row-span-3"
              >
                +
              </button>
              
              <button onClick={() => handleNumpad('1')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">1</button>
              <button onClick={() => handleNumpad('2')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">2</button>
              <button onClick={() => handleNumpad('3')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">3</button>
              
              <button onClick={() => handleNumpad('0')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100 col-span-2">0</button>
              <button onClick={() => handleNumpad('.')} className="min-h-[56px] text-2xl font-bold bg-white border border-gray-200 rounded-lg hover:bg-gray-50 active:bg-gray-100">.</button>
              
              <button 
                data-save-btn
                onClick={handleFinalSave}
                disabled={submitting || ((!weight || parseFloat(weight) <= 0) && sessionEntries.length === 0)}
                className="min-h-[56px] text-lg font-bold bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed col-span-4"
              >
                {submitting ? 'Speichern...' : 'Speichern'}
              </button>
            </div>

            {/* Tastatur-Hinweis */}
            <p className="text-xs text-center text-slate-400 mt-3">
              Tastatureingabe: Ziffern, + (hinzufuegen), Enter (speichern), Backspace (loeschen)
            </p>
          </div>
        </div>
      )}

      {/* Schritt 4: Bestätigung - Legacy Card Style */}
      {step === 'confirm' && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Bestätigen
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Strecke:</span>
                <span className="font-medium">
                  {selectedLine?.name}
                  {selectedSubLine && ` → ${selectedSubLine.name}`}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Kunde:</span>
                <span className="font-medium">{selectedCustomer?.shortName || selectedCustomer?.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Gewicht:</span>
                <span className="font-bold text-xl">{weight} kg</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetForm}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={submitting}
                className="flex-1 min-h-[56px] text-lg bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
      {gewichtZurBestaetigung !== null && (
        <GewichtRueckfrage
          kg={gewichtZurBestaetigung}
          onZurueck={() => setGewichtZurBestaetigung(null)}
          onBestaetigen={() => {
            const kg = gewichtZurBestaetigung;
            setGewichtZurBestaetigung(null);
            if (kg !== null) void sendeErfassung(kg);
          }}
        />
      )}
    </div>
  );
}
